import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import benchmarksHelper from "../../../../API/Benchmarks/benchmarksHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";

const BenchmarksList = ({ isFunctionsLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig, getWorkspaceEntities } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedBenchmarkId, setSelectedBenchmarkId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedBenchmarks, setDisplayedBenchmarks] = React.useState([]);
  const [selectedBenchmarkIds, setSelectedBenchmarkIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/benchmarks/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.3,
    },
    {
      title: t("settings.list.description"),
    },
    {
      title: t("settings.list.records"),
      width: 0.1,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  useEffect(() => {
    if (state?.benchmarks) setDisplayedBenchmarks([...state?.benchmarks]);
  }, [state?.benchmarks && JSON.stringify([...state?.benchmarks])]);

  const identifyOptByID = (id) => {
    return state?.benchmarks?.find((u) => u.id === id);
  };
  const handleBenchmarkItemSelection = ({ id, type, item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !id) return;
    history.push(`/workspaces/${wsId}/benchmarks/${id}`);
  };
  const loadBenchmarks = async (workspaceID) => {
    try {
      setIsLoading(true);
      let resultBenchmarks = await benchmarksHelper?.getBenchmarksByWorkspaceID(
        workspaceID
      );
      resultBenchmarks =
        resultBenchmarks?.data?.BenchmarksByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
      dispatch("SET_BENCHMARKS", resultBenchmarks);
    } catch (error) {
      console.error("Error loading benchmarks:", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadBenchmarks(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id]);
  const handleBenchmarkItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedBenchmarkId || selectedItem?.id;
      if (_id) {
        const result = await benchmarksHelper.deleteBenchmark(_id);
        dispatch("SET_BENCHMARKS", [
          ...state?.benchmarks.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("benchmarkDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedBenchmarkId(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("benchmarkDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedBenchmarkIds) {
        await benchmarksHelper.deleteBenchmark(id);
      }
      dispatch("SET_BENCHMARKS", [
        ...state?.benchmarks.filter(
          (u) => !selectedBenchmarkIds.includes(u.id)
        ),
      ]);
      setSelectedBenchmarkIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("benchmarkDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("benchmarkDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/benchmarks/new`);
  };
  const isItemActionable = (item) => {
    const _item = item?.item;
    if (_item) return true;
    else return false;
  };
  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteBenchmarkConfirmation")}
        subCaption={t("settings.list.deleteBenchmarkConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleBenchmarkItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteBenchmarkConfirmation"),
        })}
        subCaption={t("settings.list.deleteBenchmarkConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedBenchmarkIds]);

  return getActiveWorkspace()?.subscriptionType === "base" ? (
    <AppPlaceholderView
      accessibilityLabel="benchmarks-availability"
      wrapperStyle={{
        maxWidth: 400,
        height: 200,
        marginHorizontal: "auto",
        marginTop: 50,
      }}
      size={30}
      icon={AppAssets.icons.accessDenied}
      mainCaption={t("settings.list.benchmarksNotAvailable")}
      subCaption={t("settings.list.upgradeToEnableBenchmarks")}
    />
  ) : (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      {getPlanConfig()?.isBatchJobsEnabled ? (
        <>
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption={t("settings.list.loadingBenchmarks")}
            />
          ) : (
            <>
              {displayedBenchmarks.length > 0 ? (
                <AppTable
                  isSelectable
                  showPagination
                  itemsPerPage={50}
                  hideSearch={false}
                  searchPlaceholder={t("settings.toolbar.searchBenchmarks")}
                  onSelectionChange={(selectedItems) => {
                    setSelectedBenchmarkIds(
                      selectedItems?.map((u) => u?.id) || []
                    );
                  }}
                  bulkActions={[
                    {
                      id: "DELETE",
                      label: t("settings.list.delete"),
                      icon: AppAssets.icons.delete,
                      isDestructive: true,
                    },
                  ]}
                  onBulkActionPress={(id) => {
                    if (id === "DELETE" && selectedBenchmarkIds?.length > 0)
                      setShowBulkDeleteChoice(true);
                  }}
                  actionButtons
                  mapIconWithStatus={(item) => {
                    if (item?.status == "active" || item?.status == "syncing")
                      return AppAssets.icons.reload;
                    else return AppAssets.icons.activeModel;
                  }}
                  mainActionLabel={t("settings.list.view")}
                  showDropDownMenuForActions
                  onClickMainAction={(item, index) => {
                    handleBenchmarkItemSelection({
                      id: item?.id,
                      type: "benchmark",
                      item: item,
                      index: index,
                    });
                  }}
                  actionButtonStyle={{
                    minWidth: 20,
                  }}
                  onActionItemPress={async (id, item, index) => {
                    if (id === "EDIT") {
                      handleBenchmarkItemSelection({
                        id: item?.id,
                        type: "benchmark",
                        item: item,
                        index: index,
                      });
                    } else if (id === "DELETE") {
                      setSelectedItem(item);
                      setSelectedIndex(index);
                      setSelectedBenchmarkId(item?.id);
                      setShowChoice(true);
                    }
                  }}
                  actions={[
                    {
                      label: t("settings.list.edit"),
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },

                    {
                      label: t("settings.list.delete"),
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  isItemActionable={(item) =>
                    isItemActionable({
                      item: item,
                    })
                  }
                  style={{
                    zIndex: -1,
                    marginTop: 15,
                    height:
                      displayedBenchmarks?.length >= 50
                        ? height - 95
                        : height - 75,
                    paddingRight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedBenchmarks
                    ?.sort((a, b) =>
                      (a?.name || "").localeCompare(b?.name || "")
                    )
                    ?.map((u) => {
                      return {
                        id: u?.id || " ",
                        name: u?.name || " ",
                        description:
                          u?.description ||
                          t("settings.list.noDescriptionProvided"),
                        records:
                          typeof u?.questions == "string"
                            ? JSON.parse(u?.questions)?.length?.toString()
                            : u?.questions?.length?.toString(),
                        actions: " ",
                      };
                    })}
                >
                  <ToothFairyButton
                    title={t("settings.toolbar.create")}
                    onPress={handleCreation}
                  />
                </AppTable>
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 20,
                    marginHorizontal: "auto",
                    flex: 1,
                  }}
                  size={30}
                  icon={AppAssets.icons.benchmark}
                  mainCaption={t("settings.list.noBenchmarksFound")}
                  subCaption={t("settings.list.noBenchmarksFoundSub")}
                  actionLabel={t("settings.toolbar.create")}
                  onActionPress={handleCreation}
                />
              )}
            </>
          )}
        </>
      ) : (
        <AppPlaceholderView
          wrapperStyle={{
            width: "100%",
            height: "100%",
            marginVertical: 20,
            marginHorizontal: "auto",
            flex: 1,
          }}
          size={30}
          icon={AppAssets.icons.orbit}
          mainCaption={t("settings.list.emptyPlaceholder")}
          subCaption={t("settings.list.batchJobsNotEnabled")}
        />
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default BenchmarksList;
